"use client"

import { useState } from 'react';
import { ChevronDown } from '@/lib/icons';
import { BlogCard } from './BlogCard';

interface BlogPost {
  title: string;
  description: string;
  slug: string;
  category: string;
  author: string;
  publishDate: string;
  readTime: string;
  featured?: boolean;
  heroImage?: string | null;
}

interface ProgressiveBlogGridProps {
  posts: BlogPost[];
  showFeatured?: boolean;
}

export function ProgressiveBlogGrid({ posts, showFeatured = true }: ProgressiveBlogGridProps) {
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Unique sorted categories derived from actual posts
  const categories = Array.from(new Set(posts.map(p => p.category).filter(Boolean))).sort() as string[];

  // Filter by active category before splitting featured/regular
  const visiblePosts = activeCategory ? posts.filter(p => p.category === activeCategory) : posts;

  // Separate featured and regular posts
  const featuredPosts = visiblePosts.filter(post => post.featured);
  const regularPosts = visiblePosts.filter(post => !post.featured);

  // Control visibility - show 6 initially
  const INITIAL_VISIBLE = 6;
  const visibleRegularPosts = regularPosts.slice(0, INITIAL_VISIBLE);
  const remainingCount = regularPosts.length - INITIAL_VISIBLE;
  const hasMorePosts = regularPosts.length > INITIAL_VISIBLE;

  function handleCategoryClick(cat: string) {
    setShowAllPosts(false);
    setActiveCategory(prev => prev === cat ? null : cat);
  }

  return (
    <div className="space-y-12">

      {/* Category Filter */}
      {categories.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-500 mr-1">Browse by category:</span>
          <button
            onClick={() => { setActiveCategory(null); setShowAllPosts(false); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeCategory === null
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => handleCategoryClick(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Featured Posts Section - Always fully visible */}
      {showFeatured && featuredPosts.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Featured Articles</h2>
            <div className="h-px bg-gradient-to-r from-primary/50 to-transparent flex-1 ml-6"></div>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {featuredPosts.map((post) => (
              <BlogCard
                key={post.slug}
                {...post}
                featured={true}
              />
            ))}
          </div>
        </section>
      )}

      {/* Latest Posts Section with Progressive Reveal */}
      {regularPosts.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Latest Articles</h2>
            <div className="h-px bg-gradient-to-r from-gray-300 to-transparent flex-1 ml-6"></div>
          </div>

          {/* Visible Posts Grid */}
          <div className="relative">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {visibleRegularPosts.map((post, index) => {
                const isInLastRow = !showAllPosts && index >= 3 && index < 6;

                return (
                  <div key={post.slug} className="relative">
                    {/* Gradient overlay for last row when collapsed */}
                    {isInLastRow && hasMorePosts && (
                      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white via-white/60 to-transparent pointer-events-none z-10"></div>
                    )}
                    <BlogCard {...post} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Additional Posts with Smooth Reveal Animation */}
          {hasMorePosts && (
            <div className={`overflow-hidden transition-all duration-700 ease-out ${
              showAllPosts ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mt-6">
                {regularPosts.slice(INITIAL_VISIBLE).map((post) => (
                  <BlogCard key={post.slug} {...post} />
                ))}
              </div>
            </div>
          )}

          {/* View More/Less Button */}
          {hasMorePosts && (
            <div className="text-center mt-12">
              <button
                onClick={() => setShowAllPosts(!showAllPosts)}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-primary-200 text-primary-700 font-semibold rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                {showAllPosts ? 'Show Less' : `View More (${remainingCount})`}
                <ChevronDown className={`ml-2 w-5 h-5 transition-transform duration-200 ${
                  showAllPosts ? 'rotate-180' : ''
                }`} />
              </button>
            </div>
          )}
        </section>
      )}

      {/* Empty State */}
      {posts.length === 0 && (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles yet</h3>
            <p className="text-gray-600">Check back soon for the latest mortgage marketing insights and strategies.</p>
          </div>
        </div>
      )}
    </div>
  );
}